import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Search } from 'lucide-react'
import type { UnsplashPhoto } from '@/lib/api/features/unsplash'
import { searchPhotos } from '@/lib/api/features/unsplash'

interface UnsplashSearchProps {
  onSelect: (photo: UnsplashPhoto) => void
}

export function UnsplashSearch({ onSelect }: UnsplashSearchProps) {
  const { t } = useTranslation('common')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Array<UnsplashPhoto>>([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const doSearch = useCallback(async (searchQuery: string, searchPage: number) => {
    if (!searchQuery.trim()) {
      setResults([])
      setTotalPages(0)
      return
    }

    setIsLoading(true)
    try {
      const data = await searchPhotos(searchQuery, searchPage, 20)
      if (searchPage === 1) {
        setResults(data.results)
      } else {
        setResults((prev) => [...prev, ...data.results])
      }
      setTotalPages(data.total_pages)
    } catch {
      // Silently fail - user can retry
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (!query.trim()) {
      setResults([])
      setTotalPages(0)
      return
    }

    debounceRef.current = setTimeout(() => {
      setPage(1)
      void doSearch(query, 1)
    }, 400)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [query, doSearch])

  const loadMore = useCallback(() => {
    if (page < totalPages && !isLoading) {
      const nextPage = page + 1
      setPage(nextPage)
      void doSearch(query, nextPage)
    }
  }, [page, totalPages, isLoading, query, doSearch])

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('coverImage.searchUnsplash')}
          className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-base focus:outline-none focus:ring-2 focus:ring-ring md:text-sm"
          autoFocus
        />
      </div>

      {isLoading && results.length === 0 && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!isLoading && query.trim() && results.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">
          No photos found
        </p>
      )}

      {results.length > 0 && (
        <div className="grid grid-cols-3 gap-1.5 max-h-[300px] overflow-y-auto">
          {results.map((photo) => (
            <button
              key={photo.id}
              type="button"
              onClick={() => onSelect(photo)}
              className="relative aspect-[3/2] overflow-hidden rounded-sm hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <img
                src={photo.urls.small}
                alt={photo.alt_description || 'Unsplash photo'}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent px-1 py-0.5">
                <span className="text-[10px] text-white truncate block">
                  {photo.user.name}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {page < totalPages && results.length > 0 && (
        <button
          type="button"
          onClick={loadMore}
          disabled={isLoading}
          className="text-sm text-primary hover:underline disabled:opacity-50 py-1"
        >
          {isLoading ? 'Loading...' : 'Load more'}
        </button>
      )}
    </div>
  )
}
